import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import styles from './UrgentEndingSection.module.scss';
import { getEmergencyServiceEndingSoon } from '@/api/emergencyServiceApi';

// 使用Ant Design Icons替代Lucide React（保持项目一致性）
import { FieldTimeOutlined, EnvironmentOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

interface UrgentOrder {
  id: string | number;
  title: string;
  image: string;
  address: string;
  description: string;
  timeLeft: string;
  distance: string;
  currentBid: number;
  status: string;
}

type UrgentEndingSectionProps = {
  user_latitude?: number;
  user_longitude?: number;
};

const UrgentEndingSection: React.FC<UrgentEndingSectionProps> = ({
  user_latitude,
  user_longitude,
}) => {
  const navigate = useNavigate();
  
  // 从接口获取的数据
  const [urgentOrders, setUrgentOrders] = useState<UrgentOrder[]>([]);
  const [loading, setLoading] = useState(false);

  // 获取快要结束的紧急服务
  const fetchUrgentOrders = React.useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 3 };
      if (user_latitude && user_longitude) {
        params.user_latitude = user_latitude;
        params.user_longitude = user_longitude;
      }
      
      const res = await getEmergencyServiceEndingSoon(params);
      console.log("Urgent Ending API 返回数据:", res);
      
      if (res && res.code === 200) {
        const orderList = res.data || [];
        // 转换数据格式以匹配UI设计
        const transformedOrders = orderList.map((item: any) => ({
          id: item.emergency_service_id,
          title: item.problem_description || 'Emergency Service',
          image: item.images?.[0]?.product_media_file_url || '/api/placeholder/600/320?text=Emergency',
          address: item.address || 'Address N/A',
          description: item.problem_description || 'Emergency Service Required',
          timeLeft: `${Math.max(0, Math.round(item.hours_remaining * 60))} Minutes`,
          distance: item.distance ? `${item.distance.toFixed(1)} Miles` : 'Distance N/A',
          currentBid: item.budget || item.fixed_price || 0,
          status: 'Critical',
        }));
        
        setUrgentOrders(transformedOrders);
      } else {
        setUrgentOrders([]);
      }
    } catch (error) {
      console.error("获取紧急服务失败:", error);
      setUrgentOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user_latitude, user_longitude]);

  // 组件挂载时获取数据
  useEffect(() => {
    fetchUrgentOrders();
  }, [fetchUrgentOrders]);

  const handleOrderClick = (orderId: string | number) => {
    navigate(`/EmergencyServiceDetail/${orderId}`);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <span>Loading urgent services...</span>
        </div>
      </div>
    );
  }

  if (urgentOrders.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyContainer}>
          <ExclamationCircleOutlined className={styles.emptyIcon} />
          <h3>No Urgent Services Ending Soon</h3>
          <p>There are currently no urgent services ending soon. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 头部标题栏 */}
      <div className={styles.header}>
        <h2 className={styles.title}>
          Urgent Ending <span className={styles.subtitle}>- Bidding Ends Soon</span>
        </h2>
        <a 
          href="/emergencyService" 
          className={styles.viewMoreLink}
          onClick={(e) => {
            e.preventDefault();
            navigate('/emergencyService');
          }}
        >
          View More
        </a>
      </div>

      {/* 卡片网格布局 */}
      <div className={styles.grid}>
        {urgentOrders.map((item) => (
          <div 
            key={item.id} 
            className={styles.card}
            onClick={() => handleOrderClick(item.id)}
          >
            {/* 背景图片 */}
            <img 
              src={item.image} 
              alt={item.title} 
              className={styles.cardImage}
            />
            
            {/* 顶部悬浮标签 */}
            {/* 左侧：紧急状态 */}
            <div className={styles.statusBadge}>
              <ExclamationCircleOutlined className={styles.statusIcon} />
              {item.status}
            </div>
            {/* 右侧：距离信息 */}
            <div className={styles.distanceBadge}>
              <EnvironmentOutlined className={styles.distanceIcon} />
              {item.distance} From You
            </div>

            {/* 底部悬浮信息框 */}
            <div className={styles.infoBox}>
              {/* 标题 */}
              <h3 className={styles.cardTitle}>{item.title}</h3>
              
              {/* 地址与描述 */}
              <div className={styles.cardMeta}>
                <p className={styles.address}>Address: {item.address}</p>
                <p className={styles.description}>Problem: {item.description}</p>
              </div>

              {/* 分割线 */}
              <div className={styles.divider}></div>

              {/* 底部信息栏：时间和价格 */}
              <div className={styles.footer}>
                {/* 倒计时 */}
                <div className={styles.timeSection}>
                  <span className={styles.timeLabel}>Time Left</span>
                  <div className={styles.timeValue}>
                    <FieldTimeOutlined className={styles.timeIcon} />
                    <span className={styles.pulse}>{item.timeLeft}</span>
                  </div>
                </div>

                {/* 价格 */}
                <div className={styles.priceSection}>
                  <span className={styles.priceLabel}>Starting Bid</span>
                  <span className={styles.priceValue}>
                    ${item.currentBid}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UrgentEndingSection;
